import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { SaveDangerousDebtsRequest } from "../../../../api/ClientFileApi";
import { useBankruptcyChecklistApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { ChecklistAnswer } from "../../../BankruptcyChecklist.types";
import { useBankruptcyChecklist } from "../../../hooks/useBankruptcyChecklist";
import { DangerousDebtsFormValues } from "../DangerousDebts.types";
import { EMPTY_DANGEROUS_DEBTS, applyAnswer, mapResponseToDangerousDebts } from "../DangerousDebts.utils";

export function useDangerousDebts(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const bankruptcyChecklistApi = useBankruptcyChecklistApi();

  const { data } = useBankruptcyChecklist(clientId);

  const saveMutation = useMutation({
    mutationFn: (request: SaveDangerousDebtsRequest) =>
      bankruptcyChecklistApi.saveBankruptcyChecklistDangerousDebts({
        clientId,
        saveDangerousDebtsRequest: request,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData([ClientFileQueryKey.BankruptcyChecklist, clientId], saved);
      showSuccess(t("clients.client.debtSettlement.bankruptcyChecklist.saveSuccess"));
    },
  });

  const initialValues: DangerousDebtsFormValues = useMemo(
    () => (data ? mapResponseToDangerousDebts(data) : EMPTY_DANGEROUS_DEBTS),
    [data]
  );

  const formik = useFormikWithDirtyForm<DangerousDebtsFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await saveMutation.mutateAsync({ ...values });
    },
  });

  const setAnswer = (key: keyof DangerousDebtsFormValues, value: ChecklistAnswer) => {
    void formik.setValues(applyAnswer(formik.values, key, value));
  };

  return { formik, setAnswer };
}
